import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { IPositionOptions } from "./CrewFilterAndSort";
import { useDispatch } from "react-redux";
import { useTheme, Grid, Button, Theme, Box } from "@mui/material";
import { setSearchedCrewmembers } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IUser } from "@vmosolutions/oc-interfaces";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { debounce } from "lodash";
import { IconButton } from "src/components/IconButton/IconButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface ICrewSearchProps {
  positionHandler: (position: IPositionOptions) => void;
  currentPosition: string;
  // positionOptions: IPositionOptions[];
  timelineContainer: any;
  windowId: string;
  setTabValue: React.Dispatch<React.SetStateAction<string | boolean>>;
}

// Create a map of recently retrived search results stored by user _id to remove duplicates.
interface ISelectedUserMap {
  [userid: string]: IUser;
}

const crewSearchStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  "& .search_crew, & .position_filter, & .group_filter": {
    margin: "0px 10px",
    width: 350,
    "& .input_control label": {
      width: "auto",
      margin: "0px 10px auto auto",
      ...theme.typography.subtitle1,
    },
    "& .clear_all_btn": {
      margin: "5px",
      padding: "2px 8px",
      height: 20,
      fontSize: 10,
    },
  },
});

// Convert selection array to map.
const createSelectionMap = (selectedUsers: IUser[]): ISelectedUserMap => {
  if (selectedUsers?.length > 0) {
    return selectedUsers.reduce((map: ISelectedUserMap, user) => {
      map[user._id] = user;
      return map;
    }, {});
  }
  return {};
};

export const CrewSearch = (props: ICrewSearchProps): JSX.Element => {
  const { windowId, timelineContainer, setTabValue } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store a map of all recently searched (and selected) users;
  const [selectedUserMap, setSelectedUserMap] = useState<ISelectedUserMap>({});
  const [searchResults, setSearchResults] = useState<IUser[]>([]);
  const [focused, setFocused] = useState(false);
  const crewMembers = useSelector((state: IRootState) => state.crewSchedule.crewMembers);

  // An array containing currently selected users + any new search results (duplicated removed)
  const selectionOptions: IUser[] = useMemo(() => {
    const userMap = { ...selectedUserMap };
    if (searchResults?.length > 0) {
      searchResults.forEach((user) => {
        userMap[user._id] = user;
      });
    }
    return Object.values(userMap);
  }, [selectedUserMap, searchResults]);

  /**
   * Search users for available "active" crew members matching the provided string.
   * @param inputValue - search string
   * @returns
   */
  const searchUser = useCallback(
    (inputValue: string): ICrewMember[] => {
      if (inputValue.length < 3) return [];
      const regex = new RegExp(`.*${inputValue}.*`, "i");
      const users: ICrewMember[] = crewMembers.filter((crewMember) => {
        return (
          crewMember.firstName.match(regex) ||
          crewMember.lastName.match(regex) ||
          crewMember.preferredName?.match(regex) ||
          crewMember.middleName?.match(regex) ||
          crewMember.employeeId?.match(regex)
        );
      });
      return Array.isArray(users) ? users : [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crewMembers.length]
  );

  // If selected users change, call dispatch to update crew sched list.
  useEffect(() => {
    const selectedUsers = Object.values(selectedUserMap);
    const crewIds = selectedUsers?.map((user) => user._id) ?? [];
    dispatch(setSearchedCrewmembers(windowId, crewIds));
  }, [dispatch, selectedUserMap, windowId]);

  const findUser = useCallback(
    (newVal: string): void => {
      const usersFound = searchUser(newVal);
      const results = Array.isArray(usersFound) && usersFound?.length > 0 ? usersFound : [];
      setSearchResults(results);

      // if only one result was found, automatically select it (add it to selection map)
      if (usersFound?.length === 1) {
        const user = usersFound[0];
        setSelectedUserMap((prev) => ({ ...prev, [user._id]: user }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crewMembers.length]
  );

  const handleFindUser = useCallback(
    (newVal: string) => {
      if (!newVal || newVal.length < 2) return;
      findUser(newVal);
    },
    [findUser]
  );

  const clearRecentSearch = useCallback((): void => {
    setSelectedUserMap({});
    dispatch(setSearchedCrewmembers(windowId, []));
  }, [dispatch, windowId]);

  if (!timelineContainer) {
    return null;
  }

  return (
    <Box sx={crewSearchStyles(theme)}>
      <Grid container alignItems="center">
        <Grid item>
          <IconButton style={{ paddingBottom: "4px", marginRight: "15px" }} onClick={() => setTabValue(false)}>
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid item className="search_crew">
          <SelectField
            label="Search Crew"
            isMulti={true}
            name="displaySearchedCrew"
            menuPortalTarget={timelineContainer}
            // cacheOptions
            menuPosition="fixed"
            value={Object.values(selectedUserMap)}
            onChange={(selection) => {
              setSelectedUserMap(createSelectionMap(selection));
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            options={selectionOptions}
            onMenuClose={() => {
              // When user closes the menu, clear search results so that only selected users show.
              setSearchResults([]);
            }}
            // Debounce input so that calls aren't made everytime user types a key.
            // Call will only be made 300 milliseconds after last key press.
            onInputChange={debounce(handleFindUser, 300)}
            getOptionLabel={(user: IUser) =>
              `${user.lastName}, ${user.firstName} ${user?.employeeId ? `(${user.employeeId})` : ""}`
            }
            getOptionValue={(user: IUser) => user._id}
            isClearable={true}
            placeholder={
              focused ? "" : Object.values(selectedUserMap)?.length > 0 ? "Crew Selected" : "Search and Select Crew"
            }
            multiMode="checkbox"
          />
        </Grid>
        <Button
          className="clear_all_btn"
          id={"clear_recent_search_btn"}
          onClick={() => clearRecentSearch()}
          disabled={Object.values(selectedUserMap)?.length === 0}
          variant="outlined"
        >
          Reset
        </Button>
      </Grid>
    </Box>
  );
};
